import Icon from 'components/atoms/Icon';
import {
  CoderbyteStageActionFragment,
  useRefreshCoderbyteAssessmentDetailsMutation,
} from 'generated/graphql';
import { FC } from 'react';
import {
  Button,
  DropdownMenu,
  TextInputField,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

interface IStageCoderbyteAction {
  action: CoderbyteStageActionFragment;
}

const describe = ({ name, details }: CoderbyteStageActionFragment): string => {
  const parts = [
    details.timeLimitInMinutes !== null &&
    details.timeLimitInMinutes !== undefined
      ? `${details.timeLimitInMinutes} min`
      : null,
    details.challengeCount ? `${details.challengeCount} challenges` : null,
    details.multipleChoiceCount ? `${details.multipleChoiceCount} MCQ` : null,
  ].filter(Boolean);

  return parts.length ? `${name} (${parts.join(', ')})` : name;
};

const StageCoderbyteAction: FC<IStageCoderbyteAction> = ({ action }) => {
  const { handleMsgType } = useNotification();

  const [refreshDetails, { loading }] =
    useRefreshCoderbyteAssessmentDetailsMutation({
      variables: { id: action.id },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Coderbyte assessment refreshed',
        });
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Coderbyte assessment could not be refreshed.',
          message: 'Something went wrong. Please try again.',
        });
      },
    });

  return (
    <div
      className="stage-test-action"
      data-testid="edit-project__stage-coderbyte-action"
    >
      <Icon icon="build" className="stage-test-action__icon" />
      <TextInputField
        id={`coderbyte-action-${action.id}`}
        disabled
        onChange={() => {}}
        value={describe(action)}
        className="stage-test-action__name-label"
      />
      <DropdownMenu
        renderButton={(_, __, handleTriggerDropdown) => (
          <Button
            variant="secondary"
            size="small"
            className="stage-test-action__config-button"
            data-testid="stage-coderbyte-action__menu-button"
            onClick={handleTriggerDropdown}
            disabled={loading}
            aria-label="Coderbyte assessment actions"
          >
            <Icon
              icon="settings"
              className="stage-test-action__config-button-icon"
            />
          </Button>
        )}
        options={[
          {
            // Coderbyte locks an assessment once someone has sat it, so a
            // frozen snapshot has nothing newer to read.
            label: action.details.isFrozen
              ? 'Refresh from Coderbyte (locked)'
              : 'Refresh from Coderbyte',
            disabled: action.details.isFrozen || loading,
            onClick: () => refreshDetails(),
          },
        ]}
      />
    </div>
  );
};

export default StageCoderbyteAction;
